import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, MessageCircle, Globe2, MessagesSquare, HandHeart, Zap, User2, Building2 } from "lucide-react";
import { SEO, SITE_URL } from "@/lib/seo";
import { useWhatsApp } from "@/lib/siteSettings";

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="mb-8">
    <h2 className="mb-2 text-xl font-bold text-foreground">{title}</h2>
    <div className="space-y-2 text-sm leading-7 text-muted-foreground">{children}</div>
  </section>
);

const LegalShell = ({ children }: { children: React.ReactNode }) => (
  <section className="min-h-screen bg-background px-4 py-12 sm:px-6 lg:px-8">
    <article className="mx-auto max-w-3xl">{children}</article>
  </section>
);

const ContactLink = () => {
  const { t } = useTranslation();
  return (
    <Link className="font-bold text-primary underline-offset-4 hover:underline" to="/contact">
      {t("legal.contactLink")}
    </Link>
  );
};

export const Privacy = () => {
  const { t } = useTranslation();
  return (
    <LegalShell>
      <SEO title={t("seo.privacy.title")} description={t("seo.privacy.description")} path="/privacy" />
      <h1 className="mb-2 text-3xl font-black tracking-tight">{t("legal.privacy.title")}</h1>
      <p className="mb-8 text-sm text-muted-foreground">{t("legal.lastUpdated")}: 14-05-2026</p>
      <Section title={t("legal.privacy.collectTitle")}><p>{t("legal.privacy.collectBody")}</p></Section>
      <Section title={t("legal.privacy.useTitle")}><p className="whitespace-pre-line">{t("legal.privacy.useBody")}</p></Section>
      <Section title={t("legal.privacy.cookiesTitle")}><p>{t("legal.privacy.cookiesBody")}</p></Section>
      <Section title={t("legal.privacy.thirdPartyTitle")}><p>{t("legal.privacy.thirdPartyBody")}</p></Section>
      <Section title={t("legal.contactTitle")}><p>{t("legal.contactBody")} <ContactLink />.</p></Section>
    </LegalShell>
  );
};

export const Terms = () => {
  const { t } = useTranslation();
  return (
    <LegalShell>
      <SEO title={t("seo.terms.title")} description={t("seo.terms.description")} path="/terms" />
      <h1 className="mb-2 text-3xl font-black tracking-tight">{t("legal.terms.title")}</h1>
      <p className="mb-8 text-sm text-muted-foreground">{t("legal.lastUpdated")}: 14-05-2026</p>
      <Section title={t("legal.terms.useTitle")}><p className="whitespace-pre-line">{t("legal.terms.useBody")}</p></Section>
      <Section title={t("legal.terms.accuracyTitle")}><p>{t("legal.terms.accuracyBody")}</p></Section>
      <Section title={t("legal.terms.ipTitle")}><p>{t("legal.terms.ipBody")}</p></Section>
      <Section title={t("legal.terms.amendmentsTitle")}><p>{t("legal.terms.amendmentsBody")}</p></Section>
      <Section title={t("legal.contactTitle")}><p>{t("legal.contactBody")} <ContactLink />.</p></Section>
    </LegalShell>
  );
};

export const About = () => {
  const { t } = useTranslation();
  const { waUrl } = useWhatsApp();
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "ISTANOVA",
    url: `${SITE_URL}/about`,
  };

  const offerings = [
    { Icon: Globe2, text: t("legal.about.offering.1") },
    { Icon: MessagesSquare, text: t("legal.about.offering.2") },
    { Icon: HandHeart, text: t("legal.about.offering.3") },
    { Icon: Zap, text: t("legal.about.offering.4") },
  ];

  return (
    <LegalShell>
      <SEO title={t("seo.about.title")} description={t("seo.about.description")} path="/about" jsonLd={jsonLd} />
      <h1 className="mb-8 text-3xl font-black tracking-tight">{t("legal.about.title")}</h1>

      <Section title={t("legal.about.missionTitle")}>
        <p className="whitespace-pre-line">{t("legal.about.missionBody")}</p>
      </Section>

      <Section title={t("legal.about.howTitle")}>
        <p className="whitespace-pre-line">{t("legal.about.howBody")}</p>
      </Section>

      <section className="mb-10">
        <h2 className="mb-4 text-xl font-bold text-foreground">{t("legal.about.offeringTitle")}</h2>
        <ul className="grid gap-3 sm:grid-cols-2">
          {offerings.map(({ Icon, text }, i) => (
            <li key={i} className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <span className="text-sm leading-6 text-foreground">{text}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-10">
        <h2 className="mb-4 text-xl font-bold text-foreground">{t("legal.about.audienceTitle")}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="mb-2 flex items-center gap-2 text-primary"><User2 className="h-5 w-5" /><span className="font-bold text-foreground">{t("legal.about.audience.clientTitle")}</span></div>
            <p className="text-sm leading-6 text-muted-foreground">{t("legal.about.audience.clientBody")}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <div className="mb-2 flex items-center gap-2 text-primary"><Building2 className="h-5 w-5" /><span className="font-bold text-foreground">{t("legal.about.audience.ownerTitle")}</span></div>
            <p className="text-sm leading-6 text-muted-foreground">{t("legal.about.audience.ownerBody")}</p>
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-primary/20 bg-card p-6 shadow-soft">
        <h2 className="mb-2 text-xl font-bold text-foreground">{t("legal.about.contactTitle")}</h2>
        <p className="mb-5 text-sm text-muted-foreground">{t("legal.about.contactBody")}</p>
        <div className="flex flex-col gap-3 sm:flex-row">
          <a href={waUrl} target="_blank" rel="noreferrer" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 font-bold text-primary-foreground shadow-premium transition hover:scale-[1.02]">
            <MessageCircle className="h-4 w-4" /> {t("legal.about.ctaWhatsapp")}
          </a>
          <a href="mailto:istanovaaa@gmail.com" className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-border bg-card px-5 py-3 font-bold text-foreground transition hover:bg-secondary">
            <Mail className="h-4 w-4" /> {t("legal.about.ctaEmail")}
          </a>
        </div>
      </section>
    </LegalShell>
  );
};
